import * as React from 'react';

import { Typography } from '@mui/material';

import {
  FacilitiesUsage,
  GenderEnum,
  RoommateNumberID,
  InternetFacilityUsage,
  EneryLabelFacilityUsage,
} from '@kamernet/core/ApiClient/Kamernet';
import { useIntl } from '@kamernet/core/Intl';
import Chair from '@kamernet/icons/Chair.svg';
import ChairOff from '@kamernet/icons/Chair_off.svg';
import EnergyLabel from '@kamernet/icons/EnergyLabel.svg';
import EnergyLabelUnknown from '@kamernet/icons/EnergyLabel_unknown.svg';
import Kitchen from '@kamernet/icons/Kitchen.svg';
import KitchenOff from '@kamernet/icons/Kitchen_off.svg';
import Pets from '@kamernet/icons/Pets.svg';
import PetsOff from '@kamernet/icons/Pets_off.svg';
import Shower from '@kamernet/icons/Shower.svg';
import ShowerOff from '@kamernet/icons/Shower_off.svg';
import Toilet from '@kamernet/icons/Toilet.svg';
import ToiletOff from '@kamernet/icons/Toilet_off.svg';
import { CommonStyles } from '@kamernet/styles/CommonStyles';

import { RoommateNumberLabels } from '@kamernet/modules/Listing/messages';
import {
  FacilitesLivingRoomTranslations,
  FacilitesToiletTranslations,
  FacilitesKitchenTranslations,
  FacilitesShowerTranslations,
  FacilitesInternetTranslations,
  FacilitesEnergyLabelTranslations,
  RoommateNumberLabelTranslations,
  RoommatesGenderLabelTranslations,
} from '@kamernet/modules/Listing/translations';

import { ListingDetailsContext } from '../../ListingDetailsContext';

import {
  WifiStatusIcon,
  RoommateGenderIcon,
  RoommateNumberCountIcon,
} from './components/FacilitiesIcon';
import styles from './Details.module.css';

export const Details = () => {
  const { T } = useIntl();
  const { listingDetails } = ListingDetailsContext.useContext();

  return (
    <section className={styles.root}>
      <Typography className={CommonStyles.margin_bottom_2} variant="h5">
        {T('ldp.what_you_get')}
      </Typography>
      <div className={styles.gridContainer}>
        {listingDetails.livingRoomId && (
          <div className={styles.gridItem}>
            {listingDetails.livingRoomId === FacilitiesUsage.None ? (
              <ChairOff />
            ) : (
              <Chair />
            )}
            <Typography>
              {T(
                FacilitesLivingRoomTranslations[
                  listingDetails.livingRoomId as FacilitiesUsage
                ],
              )}
            </Typography>
          </div>
        )}
        {listingDetails.kitchenId && (
          <div className={styles.gridItem}>
            {listingDetails.kitchenId === FacilitiesUsage.None ? (
              <KitchenOff />
            ) : (
              <Kitchen />
            )}
            <Typography>
              {T(
                FacilitesKitchenTranslations[
                  listingDetails.kitchenId as FacilitiesUsage
                ],
              )}
            </Typography>
          </div>
        )}
        {listingDetails.showerId && (
          <div className={styles.gridItem}>
            {listingDetails.showerId === FacilitiesUsage.None ? (
              <ShowerOff />
            ) : (
              <Shower />
            )}
            <Typography>
              {T(
                FacilitesShowerTranslations[
                  listingDetails.showerId as FacilitiesUsage
                ],
              )}
            </Typography>
          </div>
        )}
        {listingDetails.toiletId && (
          <div className={styles.gridItem}>
            {listingDetails.toiletId === FacilitiesUsage.None ? (
              <ToiletOff />
            ) : (
              <Toilet />
            )}
            <Typography>
              {T(
                FacilitesToiletTranslations[
                  listingDetails.toiletId as FacilitiesUsage
                ],
              )}
            </Typography>
          </div>
        )}
        {listingDetails.internetAvailableId && (
          <div className={styles.gridItem}>
            <WifiStatusIcon
              internetFacility={listingDetails.internetAvailableId}
            />
            <Typography>
              {T(
                FacilitesInternetTranslations[
                  listingDetails.internetAvailableId as InternetFacilityUsage
                ],
              )}
            </Typography>
          </div>
        )}
        {listingDetails.energyId && (
          <div className={styles.gridItem}>
            {listingDetails.energyId === EneryLabelFacilityUsage.Unknonwn ? (
              <EnergyLabelUnknown />
            ) : (
              <EnergyLabel />
            )}
            <Typography>
              {listingDetails.energyId === EneryLabelFacilityUsage.Unknonwn
                ? T('ldp.what_you_get.unknown_energy_label')
                : T(
                    'ldp.what_you_get.energy_label',
                    FacilitesEnergyLabelTranslations[
                      listingDetails.energyId as EneryLabelFacilityUsage
                    ],
                  )}
            </Typography>
          </div>
        )}
        {listingDetails.housematesNumberId && (
          <div className={styles.gridItem}>
            <RoommateNumberCountIcon
              housematesNumberId={listingDetails.housematesNumberId}
            />
            <Typography>
              {listingDetails.housematesNumberId ===
                RoommateNumberID.MoreThanEight ||
              listingDetails.housematesNumberId === RoommateNumberID.NotRelevant
                ? T(
                    '%_%',
                    T(
                      RoommateNumberLabelTranslations[
                        listingDetails.housematesNumberId
                      ],
                    ),
                    T('DMN_ROOMMATES'),
                  )
                : T(
                    'ldp.what_you_get.roommates_amount',
                    RoommateNumberLabels[
                      listingDetails.housematesNumberId as RoommateNumberID
                    ],
                  )}
            </Typography>
          </div>
        )}
        {listingDetails.housematesGenderId && (
          <div className={styles.gridItem}>
            <RoommateGenderIcon
              housematesGenderId={listingDetails.housematesGenderId}
            />
            <Typography>
              {T(
                RoommatesGenderLabelTranslations[
                  listingDetails.housematesGenderId as GenderEnum
                ],
              )}
            </Typography>
          </div>
        )}

        <div className={styles.gridItem}>
          {listingDetails.candidatePetsAllowed === null ||
          listingDetails.candidatePetsAllowed ? (
            <Pets />
          ) : (
            <PetsOff />
          )}

          <Typography>
            {listingDetails.candidatePetsAllowed === null ? (
              T('ldp.what_you_get.pets_negotiable')
            ) : (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <React.Fragment>
                {listingDetails.candidatePetsAllowed
                  ? T('ldp.what_you_get.pets_allowed')
                  : T('ldp.what_you_get.no_pets')}
              </React.Fragment>
            )}
          </Typography>
        </div>
      </div>
    </section>
  );
};
